import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { DateTime } from 'luxon';

import {
  CheckboxQuestion,
  DatetimeQuestion,
  NumberQuestion,
  QuestionBase,
  HiddenQuestion,
  TextareaQuestion,
  DropdownQuestion,
  QuestionGroup
} from 'core/components/forms';
import { Client, Subscription, Plan } from 'core/models';
import { ProfileService } from 'core/services/profile.service';
import { PlansService } from 'core/services/plans.service';
import { NO_EDITOR_PLAN } from '../assets/constants';
import { UtilsQuestionsService } from 'core/services';
import { ROLES } from 'core/services/assets/constants';

@Injectable()
export class SubscriptionQuestionService {
  private questions: any[];
  private planQuestions: any[];
  private userRole: any;

  constructor(
    private profileService: ProfileService,
    public plansService: PlansService,
    private utilsQuestionsService: UtilsQuestionsService
  ) {
    this.profileService.getLoggedUserRole$().subscribe((userRole) => {
      this.userRole = userRole;
    });
  }

  public getSubscriptionQuestions(subscription?: Subscription, client?: Client) {
    const now = new Date();
    const hasSubscriptionStarted = subscription && now > new Date(subscription.startDate);
    const isSubscriptionExpired = subscription?.endDate && now > new Date(subscription.endDate);
    const timezone = client ? client.timezone : 'Etc/UTC';
    const startDate = subscription
      ? new Date(subscription.startDate)
      : DateTime.local().setZone(timezone).startOf('day').toJSDate();
    const startDateEditable = !(subscription && this.userRole.name === 'distributor') && !hasSubscriptionStarted;
    const rateConsumed = subscription ? subscription.rateConsumed : 0;

    const questions: QuestionBase<any>[] = [
      this.getClientIdQuestion(client ? client.id : null),
      this.getTypeQuestion(),
      new NumberQuestion({
        key: 'rateConsumed',
        label: 'subscriptions.usersRateConsumed',
        class: 'col-2',
        editable: false,
        value: rateConsumed,
        order: 3
      }),
      new NumberQuestion({
        key: 'rateLimit',
        label: 'subscriptions.usersRateLimit',
        class: 'col-2',
        validators: {
          required: true,
          minValue: {
            limit: this.userRole.name === ROLES.DISTRIBUTOR ? subscription?.rateLimit ?? 0 : rateConsumed
          }
        },
        editable: !isSubscriptionExpired,
        value: subscription ? subscription.rateLimit : null,
        order: 4
      }),
      this.getStartDateQuestion(startDate, startDate, timezone, startDateEditable),
      this.getEndDateQuestion(subscription, timezone),
      this.getCommentsQuestion(subscription ? subscription.comments : null),
      this.getSubscriptionActiveQuestion(subscription)
    ];

    this.questions = questions.filter((question) => question != null).sort((a, b) => a.order - b.order);
    return this.questions;
  }

  public getSubscriptionRenewalQuestions(
    currentSubscription?: Subscription,
    subscriptionRenewal?: Subscription,
    client?: Client
  ) {
    const currentSubscriptionEndDate = new Date(currentSubscription.endDate);
    const timezone = client ? client.timezone : 'Etc/UTC';
    const startDate = subscriptionRenewal
      ? new Date(subscriptionRenewal.startDate)
      : DateTime.fromJSDate(currentSubscriptionEndDate).setZone(timezone).plus({ seconds: 1 }).toJSDate();
    const startDateEditable = this.userRole.name !== 'distributor';

    const questions: QuestionBase<any>[] = [
      this.getClientIdQuestion(client ? client.id : null),
      this.getTypeQuestion(),
      new HiddenQuestion({
        key: 'rateConsumed',
        value: 0,
        order: 3
      }),
      new NumberQuestion({
        key: 'rateLimit',
        label: 'subscriptions.usersRateLimit',
        class: 'col-1',
        validators: {
          required: true,
          minValue: {
            limit: 0
          }
        },
        value: subscriptionRenewal ? subscriptionRenewal.rateLimit : null,
        eventHandlers: {
          onChange: (rateLimit) => {
            const keepStudents = rateLimit >= currentSubscription.rateLimit;
            const newKeepStudentsQuestion = this.getKeepStudentsQuestion(keepStudents, currentSubscription, {
              rateLimit
            });
            const keepStudentsQuestionIndex = this.utilsQuestionsService.findQuestionIndex(
              'keepStudents',
              this.questions
            );
            this.questions[keepStudentsQuestionIndex] = newKeepStudentsQuestion;
          }
        },
        order: 4
      }),
      this.getStartDateQuestion(startDate, currentSubscriptionEndDate, timezone, startDateEditable),
      this.getEndDateQuestion(subscriptionRenewal ? subscriptionRenewal : { startDate }, timezone),
      this.getCommentsQuestion(subscriptionRenewal ? subscriptionRenewal.comments : null),
      this.getKeepStudentsQuestion(currentSubscription.renewal?.keepStudents, currentSubscription, subscriptionRenewal),
      this.getSubscriptionActiveQuestion(subscriptionRenewal)
    ];

    this.questions = questions.filter((question) => question != null).sort((a, b) => a.order - b.order);
    return this.questions;
  }

  public getPlanQuestions(plan: Plan, client: Client) {
    const timezoneSubject = new BehaviorSubject(null);
    const editorAccess = plan && plan.editorAccess;
    timezoneSubject.next(client.timezone);
    const timezone = client ? client.timezone : 'Etc/UTC';
    const startDate = plan.startDate
      ? new Date(plan.startDate)
      : !client
      ? DateTime.local().setZone(timezone).startOf('day').toJSDate()
      : null;
    const endDate = plan.endDate
      ? new Date(plan.endDate)
      : !client
      ? DateTime.fromJSDate(startDate).setZone(timezone).plus({ year: 1, seconds: -1 }).toJSDate()
      : null;

    this.planQuestions = [
      new QuestionGroup({
        childQuestions: [
          new DropdownQuestion({
            key: 'type',
            class: 'col-1',
            label: 'nouns.planType',
            value: editorAccess ? plan.type : NO_EDITOR_PLAN,
            editable: !client.stripeCustomerId,
            placeholder: 'nouns.plans.none',
            optionsObservable: this.plansService.getBasePlanOptionsObservable()
          }),
          new DatetimeQuestion({
            key: 'startDate',
            label: 'nouns.startDate',
            class: 'col-2',
            editable: !client.stripeCustomerId,
            value: startDate,
            validators: { required: editorAccess },
            eventHandlers: {
              onChange: (newStartDate, startDateQuestion) => {
                if (newStartDate) {
                  const newEndDate = DateTime.fromJSDate(newStartDate)
                    .setZone(timezone)
                    .plus({ year: 1, seconds: -1 })
                    .toJSDate();
                  startDateQuestion.form.controls['endDate'].setValue(newEndDate);
                }
              }
            }
          }),
          new DatetimeQuestion({
            key: 'endDate',
            label: 'nouns.endDate',
            class: 'col-2',
            editable: !client.stripeCustomerId,
            defaultHour: 23,
            defaultMinute: 59,
            defaultSeconds: 59,
            value: endDate,
            validators: { required: editorAccess }
          })
        ]
      })
    ];
    return this.planQuestions;
  }

  private getClientIdQuestion(clientId) {
    return new HiddenQuestion({
      key: 'clientId',
      value: clientId,
      validators: { required: true },
      order: 1
    });
  }

  private getTypeQuestion() {
    return new HiddenQuestion({
      key: 'type',
      value: 'users',
      validators: { required: true },
      order: 2
    });
  }

  private getStartDateQuestion(startDate, minDate, timezone, editable) {
    return new DatetimeQuestion({
      key: 'startDate',
      label: 'nouns.startDate',
      class: 'col-2',
      value: startDate,
      minDate: minDate,
      editable,
      timezone,
      validators: { required: true },
      eventHandlers: {
        onChange: (newStartDate, startDateQuestion) => {
          if (editable && newStartDate && newStartDate instanceof Date && !isNaN(newStartDate.getTime())) {
            this.onStartDateChanged(newStartDate, startDateQuestion);
          }
        }
      },
      order: 6
    });
  }

  private getEndDateQuestion(subscription, timezone) {
    const startDate = subscription?.startDate
      ? new Date(subscription.startDate)
      : DateTime.local().setZone(timezone).startOf('day').toJSDate();
    const endDate = subscription?.endDate
      ? new Date(subscription.endDate)
      : DateTime.fromJSDate(startDate).setZone(timezone).plus({ year: 1, seconds: -1 }).toJSDate();
    const subscriptionHasRenewal = subscription && !!subscription.renewal;
    return new DatetimeQuestion({
      key: 'endDate',
      label: 'nouns.endDate',
      class: 'col-2',
      defaultHour: 23,
      defaultMinute: 59,
      defaultSeconds: 59,
      editable: this.userRole.name !== 'distributor' && !subscriptionHasRenewal,
      value: endDate,
      minDate: startDate,
      timezone,
      validators: { required: true },
      order: 7
    });
  }

  private getCommentsQuestion(value) {
    return new TextareaQuestion({
      key: 'comments',
      label: 'credits.header.comments',
      class: 'col-1',
      value,
      order: 8
    });
  }

  private getKeepStudentsQuestion(keepStudents, currentSubscription, subscriptionRenewal?) {
    const editable = subscriptionRenewal && subscriptionRenewal.rateLimit >= currentSubscription.rateLimit;
    return new CheckboxQuestion({
      key: 'keepStudents',
      label: 'subscriptions.keepStudents',
      class: 'col-1',
      value: editable ? keepStudents : false,
      editable,
      order: 9
    });
  }

  private getSubscriptionActiveQuestion(subscription) {
    return this.userRole.name !== 'distributor'
      ? new CheckboxQuestion({
          key: 'active',
          label: 'subscriptions.active',
          class: 'col-1',
          value: subscription ? subscription.active : true,
          order: 10
        })
      : new HiddenQuestion({
          key: 'active',
          value: subscription ? subscription.active : true
        });
  }

  /**
   * Set the end date of the question form
   * @param newStartDate selected the new date
   * @param startDateQuestion the start date question
   */
  private onStartDateChanged(newStartDate, startDateQuestion) {
    const newEndDate = DateTime.fromJSDate(newStartDate)
      .setZone(startDateQuestion.timezone)
      .plus({ year: 1, seconds: -1 })
      .toJSDate();
    startDateQuestion.form.controls['endDate'].setValue(newEndDate);
  }
}
